import { useCallback, useEffect, useRef, useState } from 'react';

type ContainerSize = [number, number];

interface UseResizeContainerReturn {
  containerRef: React.RefObject<HTMLDivElement>;
  containerSize: ContainerSize;
  dispatchResize: () => void;
}

export function useResizeContainer(
  initialSize: ContainerSize = [400, 300],
  widthOffset: number = 0,
  heightOffset: number = 2,
): UseResizeContainerReturn {
  const containerRef = useRef<HTMLDivElement>(null);
  const hasObservers = useRef(false);
  const [containerSize, setContainerSize] = useState<ContainerSize>(initialSize);

  const dispatchResize = useCallback(() => {
    if (hasObservers.current) return;

    const handleResize = (entries: ResizeObserverEntry[]) => {
      if (!entries || !entries.length) return;
      const entry = entries[0];
      const { width, height } = entry.contentRect;
      const newSize: ContainerSize = [width - widthOffset, height - 2];

      setContainerSize(newSize);
    };

    const ro = new window.ResizeObserver(handleResize);

    if (containerRef.current) {
      ro.observe(containerRef.current);
      hasObservers.current = true;
    }
  }, [heightOffset, widthOffset]);

  useEffect(() => {
    dispatchResize();
    return () => {
      hasObservers.current = false;
    };
  }, [dispatchResize]);

  return { containerRef, containerSize, dispatchResize };
}
